import { TellentClientAuthErrorCode, TellentClientAuthInitData } from '@core/tellent-types';

export abstract class TellentClientAuthError extends Error {
  public message: TellentClientAuthErrorCode;

  public abstract errorPayload: Partial<TellentClientAuthInitData> | null;

  public get payload(): Partial<TellentClientAuthInitData> | null {
    const payload = this.errorPayload;
    return {
      ...(this.errorPayload || {}),
      error: true,
      continueOnError: !!(payload?.accessToken && payload?.user),
    };
  }

  constructor(message: TellentClientAuthErrorCode) {
    super(message);
    this.name = 'TellentClientAuthError';
  }
}

export class TellentClientBootstrapError extends TellentClientAuthError {
  public errorPayload = null;

  constructor() {
    super(TellentClientAuthErrorCode.BootstrapError);
  }
}

/**
 * Raised when the org guid is not available in the query param, local storage or the auth token.
 */
export class TellentClientOrgGuidUnavailableError extends TellentClientAuthError {
  constructor(public errorPayload: Partial<TellentClientAuthInitData>) {
    super(TellentClientAuthErrorCode.OrgGuidUnavailable);
  }
}

/**
 * Raised when the org guid cannot be matched with any seat of the current user.
 */
export class TellentClientOrgGuidMismatchError extends TellentClientAuthError {
  constructor(public errorPayload: Partial<TellentClientAuthInitData>) {
    super(TellentClientAuthErrorCode.OrgGuidMismatch);
  }
}

/**
 * Raised when a token could not be retrieved
 */
export class TellentClientTokenUnavailableError extends TellentClientAuthError {
  public errorPayload = null;
  constructor() {
    super(TellentClientAuthErrorCode.TokenUnavailable);
  }
}

/**
 * Raised when a token is invalid (401)
 */
export class TellentClientTokenInvalidError extends TellentClientAuthError {
  public errorPayload = null;
  constructor() {
    super(TellentClientAuthErrorCode.TokenInvalid);
  }
}

/**
 * Raised when the user could not be retrieved
 */
export class TellentClientUserUnavailableError extends TellentClientAuthError {
  public errorPayload = null;
  constructor() {
    super(TellentClientAuthErrorCode.UserUnavailable);
  }
}

/**
 * Raised when the member is not found in the org.
 */
export class TellentClientMemberNotFoundInOrgError extends TellentClientAuthError {
  constructor(public errorPayload: Partial<TellentClientAuthInitData>) {
    super(TellentClientAuthErrorCode.MemberNotFoundInOrg);
  }
}

/**
 * Raised when the member seat is not found in the org.
 */
export class TellentClientMemberSeatNotFoundInOrgError extends TellentClientAuthError {
  constructor(public errorPayload: Partial<TellentClientAuthInitData>) {
    super(TellentClientAuthErrorCode.MemberSeatNotFoundInOrg);
  }
}

export function authErrorHandlerExhaustiveGuard(error: never) {
  throw new Error(
    'TellentClientAuthError handler exhaustive guard. Please handle all TellentClientAuthErrorMsg error cases.',
  );
}
